"use client";

import { AspectRatioPicture } from "@faire/deprecated-retailer-visitor-shared/components/Image/AspectRatioPicture";
import {
  ProgressiveProductImage,
  ImageTransformationType,
  Transformation,
} from "@faire/deprecated-retailer-visitor-shared/components/Image/ProgressiveProductImage";
import { EAEAEA_PIXEL } from "@faire/deprecated-retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { ImageOverlay } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ImageOverlay";
import { ProductImageWithSrcsetSizes } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductImageWithSrcsetSizes";
import { VERTICAL_ASPECT_RATIO } from "@faire/deprecated-retailer-visitor-shared/lib/verticalAspectRatio";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import {
  optimizeProductImageUrl,
  optimizeProductImageUrlFillVertical,
} from "@faire/web/common/images/optimizeImages";
import { useLocalNum } from "@faire/web/common/localization/useLocalNum";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web/common/media";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import isChromatic from "chromatic/isChromatic";
import * as React from "react";

/* eslint-disable complexity */

export interface IProductImageProps {
  children?: React.ReactNode | undefined;
  image: IImage | undefined;
  alt?: string;
  imageDimension?: ResponsiveValues<number>;
  showVerticalImagery?: boolean;
  extraProductsCount?: number;
  smallCount?: boolean;
  useLazyImageLoading?: boolean;
  shouldIncrementallyLoadImages?: boolean;
  imageBreakpoints?: string;
  onLoad?: () => void;
}

const DEFAULT_IMAGE_SIZE = 350;

export const COMMON_TILE_IMAGE_DIMENSION: IProductImageProps["imageDimension"] =
  {
    mobileAndAbove: 370,
    tabletAndAbove: 350,
  };

const getImageSource = (
  image: IImage,
  isSSR: boolean,
  dimension: number,
  showVerticalImagery: boolean = false
): string | undefined => {
  return showVerticalImagery
    ? optimizeProductImageUrlFillVertical(image, isSSR, dimension)
    : optimizeProductImageUrl(image, isSSR, dimension);
};

const useGetImageDimension = (
  imageDimension: ResponsiveValues<number> | undefined
) => {
  const { isXLargeAndAbove, isDesktopAndAbove, isTabletAndAbove, isMobile } =
    useViewport();

  if (isXLargeAndAbove && imageDimension?.xLargeAndAbove) {
    return imageDimension?.xLargeAndAbove;
  }
  if (isDesktopAndAbove && imageDimension?.desktopAndAbove) {
    return imageDimension?.desktopAndAbove;
  }
  if (isTabletAndAbove && imageDimension?.tabletAndAbove) {
    return imageDimension?.tabletAndAbove;
  }
  if (isMobile && imageDimension?.mobileAndAbove) {
    return imageDimension?.mobileAndAbove;
  }
  return DEFAULT_IMAGE_SIZE;
};

const ProductImageBase: React.FC<IProductImageProps> = (props) => {
  const {
    image,
    alt,
    imageDimension,
    showVerticalImagery,
    extraProductsCount,
    smallCount,
    useLazyImageLoading,
    shouldIncrementallyLoadImages,
    imageBreakpoints,
    onLoad,
  } = props;

  const isSSR = useIsSSR();
  const { localNum } = useLocalNum();
  const dataTestId = `product-image${showVerticalImagery ? "-vertical" : ""}`;
  const imageDimensionForViewport = useGetImageDimension(imageDimension);

  const transformation = React.useMemo(
    () =>
      showVerticalImagery
        ? ({
            type: ImageTransformationType.ProductFillVertical,
            width: imageDimensionForViewport,
          } as Transformation)
        : ({
            type: ImageTransformationType.Product,
            width: imageDimensionForViewport,
          } as Transformation),
    [showVerticalImagery, imageDimensionForViewport]
  );

  const formattedExtraProductsCount = extraProductsCount
    ? localNum(extraProductsCount)
    : undefined;

  if (!image?.url) {
    return (
      <AspectRatioPicture
        $width="100%"
        $ratio={showVerticalImagery ? VERTICAL_ASPECT_RATIO : 1}
        data-test-id={dataTestId}
        $hideBorder
      >
        <img
          alt={alt ?? image?.alt_text}
          src={EAEAEA_PIXEL}
          onLoad={onLoad}
          className="rounded-fs-container-default"
        />
      </AspectRatioPicture>
    );
  }

  const getSource = (minWidth: number, dimension: number) => {
    return (
      <source
        srcSet={getImageSource(image, isSSR, dimension, showVerticalImagery)}
        media={`(min-width: ${minWidth}px)`}
      />
    );
  };

  if (imageBreakpoints) {
    return (
      <ProductImageWithSrcsetSizes
        image={image}
        alt={alt ?? image.alt_text ?? ""}
        loading={useLazyImageLoading ? "lazy" : undefined}
        sizes={imageBreakpoints}
        showVerticalImagery={showVerticalImagery}
        formattedExtraProductsCount={formattedExtraProductsCount}
        smallCount={smallCount}
        onLoad={onLoad}
      />
    );
  }

  return (
    <AspectRatioPicture
      $width="100%"
      $ratio={showVerticalImagery ? VERTICAL_ASPECT_RATIO : 1}
      $hideBorder
      data-test-id={dataTestId}
    >
      {shouldIncrementallyLoadImages && !isChromatic() ? (
        <ProgressiveProductImage
          image={image}
          alt={alt ?? image.alt_text!}
          loading={useLazyImageLoading ? "lazy" : undefined}
          transformation={transformation}
          isSSR={isSSR}
          onLoad={onLoad}
        />
      ) : (
        <>
          {imageDimension?.xLargeAndAbove
            ? getSource(
                MIN_WIDTH_BREAKPOINTS.XLARGE,
                imageDimension.xLargeAndAbove
              )
            : null}
          {imageDimension?.desktopAndAbove
            ? getSource(
                MIN_WIDTH_BREAKPOINTS.DESKTOP,
                imageDimension.desktopAndAbove
              )
            : null}
          {imageDimension?.tabletAndAbove
            ? getSource(
                MIN_WIDTH_BREAKPOINTS.TABLET,
                imageDimension.tabletAndAbove
              )
            : null}
          {imageDimension?.mobileAndAbove
            ? getSource(
                MIN_WIDTH_BREAKPOINTS.MOBILE,
                imageDimension.mobileAndAbove
              )
            : null}
          <img
            alt={alt ?? image.alt_text!}
            src={getImageSource(
              image,
              isSSR,
              DEFAULT_IMAGE_SIZE,
              showVerticalImagery
            )}
            loading={useLazyImageLoading ? "lazy" : undefined}
            onLoad={onLoad}
            className="rounded-fs-container-default"
          />
        </>
      )}
      <ImageOverlay
        formattedExtraProductsCount={formattedExtraProductsCount}
        smallCount={smallCount}
      />
    </AspectRatioPicture>
  );
};

export const ProductImage = ProductImageBase;
